import { useField } from 'formik'
import { useTw } from '../Styles'
import { ExclamationCircleIcon } from '@heroicons/react/solid'

type BtnProps = {
  text: string
  strong?: boolean
  disabled?: boolean
  onClick: () => void
  type?: 'button' | 'submit' | 'reset'
  kind?: 'primary' | 'secondary'
}

export const ButtonStyle = {
  primary:
    'border-foreground text-white bg-foreground hover:bg-foreground focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-foreground active:bg-foreground',
  secondary:
    'border-gray-300 bg-white text-gray-700 hover:text-gray-500 focus:outline-none focus:border-foreground active:text-gray-800 active:bg-gray-100 focus:ring-2 focus:ring-foreground',
}

export function Button({
  text,
  strong,
  onClick,
  disabled,
  kind = 'primary',
  type = 'button',
}: BtnProps) {
  const tw = useTw()
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={tw`
        select-none
        w-full
        leading-5
        inline-flex
        items-center
        justify-center
        px-4
        py-2
        border
        rounded-md
        transition
        ease-in-out
        duration-150
        focus:outline-none
        ${ButtonStyle[kind]}
        ${strong ? 'font-bold' : 'font-medium text-sm'}
        ${
          disabled
            ? 'opacity-60 pointer-events-none cursor-default'
            : 'opacity-100'
        }
      `}
    >
      {text}
    </button>
  )
}

export function ButtonLink({
  text,
  onClick,
  disabled,
  type = 'button',
}: Omit<BtnProps, 'strong'>) {
  const tw = useTw()
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={tw`text-foreground`}
    >
      {text}
    </button>
  )
}

type LinkBtnProps = {
  text: string
  strong?: boolean
  kind?: 'primary' | 'secondary'
  href: string
  target: '_self' | '_blank'
}

export function LinkButton({
  text,
  strong,
  kind = 'primary',
  href,
  target,
}: LinkBtnProps) {
  const tw = useTw()
  return (
    <a
      href={href}
      target={target}
      rel="noreferrer noopener"
      className={tw`
        select-none
        w-full
        leading-5
        inline-flex
        items-center
        justify-center
        px-4
        py-2
        border
        rounded-md
        transition
        ease-in-out
        duration-150
        focus:outline-none
        ${ButtonStyle[kind]}
        ${strong ? 'font-bold' : 'font-medium text-sm'}
      `}
    >
      {text}
    </a>
  )
}

export function Input({
  label,
  name,
  type = 'text',
}: {
  label: string
  name: string
  type?: 'text' | 'email'
}) {
  const tw = useTw()
  const [field, meta] = useField(name)

  return (
    <div className={tw`w-full pb-2`}>
      <label
        htmlFor={name}
        className={tw`block text-sm font-medium leading-5 text-neutral-500`}
      >
        {label}
      </label>
      <div className={tw`mt-1 relative w-full`}>
        <input
          id={name}
          {...field}
          type={type}
          aria-invalid={!!meta.error}
          aria-describedby={`${name}-error`}
          className={tw`
            shadow-sm
            rounded-md
            block
            w-full
            sm:text-sm
            border-neutral-300
            ${
              meta.error
                ? 'pr-8 focus:ring-red-600 text-red-600 focus:border-red-600'
                : 'focus:ring-foreground focus:border-foreground'
            }`}
        />
        {meta?.error ? (
          <div
            className={tw`absolute top-2 mt-0.5 right-2 flex items-center pointer-events-none`}
          >
            <ExclamationCircleIcon className={tw`h-5 w-5 text-red-600`} />
          </div>
        ) : null}
      </div>
    </div>
  )
}
